import {
  MessageAttributeValue,
  SendMessageCommand,
  SQSClient,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, inArray } from "drizzle-orm"
import {
  partyRegistration,
  partyRegistrationOperator,
  partyRegistrationType,
} from "@tc/drizzle-v2"
import { CollectionName } from "../collection-names"
const sqs_client = new SQSClient({})
const queue_url = process.env["QUEUE_URL"]

export type Address = {
  physical_address?: string | null
  physical_city?: string | null
  physical_state?: string | null
  physical_country_code?: string | null
  physical_city_postcode?: string | null
}

export type RegistrationDoc = {
  id: string
  registration_name: string | null
  registration_type: number | null
  registration_type_code: string | null
  status: string | null
  effective_date: number | null
  nzbn: string | null
  expiry_date: number | null
  organisation_name: string | null
  address?: Address
  physical_address?: string | null
  physical_city?: string | null
  physical_state?: string | null
  physical_country_code?: string | null
  physical_city_postcode?: string | null
  accessible_by: string[]
  type_code: string | null
}

const extractCityAndPostcode = (cityField: string | null) => {
  if (!cityField) return { city: "", postcode: "" }
  const match = cityField.match(/^(.+?)\s+([A-Z0-9]+)$/i)
  return match
    ? { city: match[1].trim(), postcode: match[2] }
    : { city: cityField, postcode: "" }
}

/** Build a RegistrationDoc from raw DB row data */
const buildRegistrationDoc = (
  registrationItem: {
    registration_id: string
    registration_name: string | null
    registration_type: number | null
    status: string | null
    effective_date: Date | null
    nzbn: string | null
    expiry_date: Date | null
    type_code: string | null
  },
  registrationOperator?: {
    organisation_name: string | null
    physical_address: string | null
    physical_city: string | null
    physical_state: string | null
    physical_country_code: string | null
  },
): RegistrationDoc => {
  const { city, postcode } = extractCityAndPostcode(
    registrationOperator?.physical_city ?? null,
  )

  return {
    id: registrationItem.registration_id,
    registration_name: registrationItem.registration_name ?? "",
    registration_type: registrationItem.registration_type ?? 0,
    registration_type_code: registrationItem.type_code ?? null,
    status: registrationItem.status ?? "",
    effective_date: registrationItem.effective_date
      ? Math.floor(registrationItem.effective_date.getTime() / 1000)
      : null,
    nzbn: registrationItem.nzbn ?? "",
    expiry_date: registrationItem.expiry_date
      ? Math.floor(registrationItem.expiry_date.getTime() / 1000)
      : null,
    organisation_name: registrationOperator?.organisation_name ?? "",
    address: registrationOperator
      ? {
          physical_address: registrationOperator.physical_address ?? "",
          physical_city: city,
          physical_state: registrationOperator.physical_state ?? "",
          physical_country_code:
            registrationOperator.physical_country_code ?? "",
          physical_city_postcode: postcode,
        }
      : {
          physical_address: "",
          physical_city: "",
          physical_state: "",
          physical_country_code: "",
          physical_city_postcode: "",
        },
    physical_address: registrationOperator?.physical_address ?? "",
    physical_city: city,
    physical_state: registrationOperator?.physical_state ?? "",
    physical_country_code: registrationOperator?.physical_country_code ?? "",
    physical_city_postcode: postcode,
    accessible_by: [registrationItem.registration_name].filter(
      (v): v is string => v !== null,
    ),
    type_code: registrationItem.type_code ?? null,
  }
}
export const getRegistrationRequestData = async (
  tx: DbTransaction,
  id: string,
): Promise<RegistrationDoc | undefined> => {
  const [registrationItem] = await tx
    .select({
      registration_id: partyRegistration.id,
      registration_name: partyRegistration.identifier,
      registration_type: partyRegistration.registrationTypeId,
      status: partyRegistration.registrationStatus,
      effective_date: partyRegistration.effectiveDate,
      nzbn: partyRegistration.nzbn,
      expiry_date: partyRegistration.expiryDate,
      type_code: partyRegistrationType.typeCode,
    })
    .from(partyRegistration)
    .leftJoin(
      partyRegistrationType,
      eq(partyRegistration.registrationTypeId, partyRegistrationType.id),
    )
    .where(eq(partyRegistration.id, id))

  if (!registrationItem) {
    console.error(`Registration not found for id: ${id}`)
    return undefined
  }

  const [registrationOperator] = await tx
    .select({
      organisation_name: partyRegistrationOperator.displayName,
      physical_address: partyRegistrationOperator.physicalAddress,
      physical_city: partyRegistrationOperator.city,
      physical_state: partyRegistrationOperator.state,
      physical_country_code: partyRegistrationOperator.countryCode,
    })
    .from(partyRegistrationOperator)
    .where(eq(partyRegistrationOperator.id, id))

  return buildRegistrationDoc(registrationItem, registrationOperator)
}

/** Bulk fetch — batch queries for a chunk of IDs, returns Map<id, RegistrationDoc> */
export const getRegistrationBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, RegistrationDoc>> => {
  if (ids.length === 0) return new Map()

  const BATCH_SIZE = 2000

  // 1. Batch fetch all registration rows with type info
  type RegistrationRow = {
    registration_id: string
    registration_name: string | null
    registration_type: number | null
    status: string | null
    effective_date: Date | null
    nzbn: string | null
    expiry_date: Date | null
    type_code: string | null
  }

  const registrationRows: RegistrationRow[] = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE) as string[]
    const batchRows = await tx
      .select({
        registration_id: partyRegistration.id,
        registration_name: partyRegistration.identifier,
        registration_type: partyRegistration.registrationTypeId,
        status: partyRegistration.registrationStatus,
        effective_date: partyRegistration.effectiveDate,
        nzbn: partyRegistration.nzbn,
        expiry_date: partyRegistration.expiryDate,
        type_code: partyRegistrationType.typeCode,
      })
      .from(partyRegistration)
      .leftJoin(
        partyRegistrationType,
        eq(partyRegistration.registrationTypeId, partyRegistrationType.id),
      )
      .where(inArray(partyRegistration.id, batch))
    registrationRows.push(...batchRows)
  }

  if (registrationRows.length === 0) return new Map()

  // 2. Batch fetch all registration operators
  type OperatorRow = {
    id: string
    organisation_name: string | null
    physical_address: string | null
    physical_city: string | null
    physical_state: string | null
    physical_country_code: string | null
  }

  const operatorRows: OperatorRow[] = []
  const registrationIds = registrationRows.map((r) => r.registration_id)
  for (let i = 0; i < registrationIds.length; i += BATCH_SIZE) {
    const batch = registrationIds.slice(i, i + BATCH_SIZE)
    const batchRows = await tx
      .select({
        id: partyRegistrationOperator.id,
        organisation_name: partyRegistrationOperator.displayName,
        physical_address: partyRegistrationOperator.physicalAddress,
        physical_city: partyRegistrationOperator.city,
        physical_state: partyRegistrationOperator.state,
        physical_country_code: partyRegistrationOperator.countryCode,
      })
      .from(partyRegistrationOperator)
      .where(inArray(partyRegistrationOperator.id, batch))
    operatorRows.push(...batchRows)
  }

  // Index operators by ID for fast lookup
  const operatorById = new Map<string, OperatorRow>()
  for (const op of operatorRows) {
    operatorById.set(op.id, op)
  }

  // 3. Build docs
  const result = new Map<string, RegistrationDoc>()
  for (const row of registrationRows) {
    const operator = operatorById.get(row.registration_id)
    result.set(row.registration_id, buildRegistrationDoc(row, operator))
  }

  return result
}
export const saveRegistrationToSearch = async (
  tx: DbTransaction,
  registrationId: string,
  collectionName: string = CollectionName.Registrations,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getRegistrationRequestData(tx, registrationId)
  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${registrationId}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }
  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
